// ── Supabase config ───────────────────────────────────────────────
export const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL      || "";
export const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

// ── Auth token resolver ───────────────────────────────────────────
// Returns the signed-in user's Supabase JWT when present so RLS sees
// the authenticated user; otherwise falls back to the anon key (e.g.,
// on the login screen). The `apikey` header in each fetch stays = anon
// key — Supabase requires it as the project identifier, independent of
// who is calling. RLS policies that check auth.uid()/auth.role() need
// the JWT in the Authorization header to work.
export function getAuthToken() {
  if (typeof localStorage === "undefined") return SUPABASE_ANON_KEY;
  return localStorage.getItem("sb_session") || SUPABASE_ANON_KEY;
}

// Silently mint a fresh access token from the stored refresh token. Supabase access
// tokens expire (~1h); without this, a long-open session 401s on every authed call
// (e.g. the Fleet auto-refresh / trip sync silently going stale). Returns the new
// token, or null if refresh failed. Safe to call often — it no-ops without a refresh token.
export async function refreshSession() {
  const rt = typeof localStorage !== "undefined" ? localStorage.getItem("sb_refresh") : null;
  if (!rt) return null;
  try {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    const d = await r.json();
    if (d.access_token) {
      localStorage.setItem("sb_session", d.access_token);
      if (d.refresh_token) localStorage.setItem("sb_refresh", d.refresh_token);
      return d.access_token;
    }
  } catch (e) { /* network/parse — treat as failed refresh */ }
  return null;
}

// ── Storage: short-lived signed URL for a PRIVATE bucket object ────
// job-photos is private; reads go through a signed URL. RLS (sql/028) gates the
// signing — only an operator whose org owns the object's job can mint one.
// `path` is the object key stored in jobs.photo_url ("<job_id>/<filename>").
// Returns the absolute URL, or null on failure.
export async function getSignedUrl(bucket, path, expiresIn = 3600) {
  if (!path) return null;
  const key = String(path).split("/").map(encodeURIComponent).join("/");
  try {
    const r = await fetch(`${SUPABASE_URL}/storage/v1/object/sign/${bucket}/${key}`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
      body: JSON.stringify({ expiresIn }),
    });
    if (!r.ok) return null;
    const d = await r.json();
    return d?.signedURL ? `${SUPABASE_URL}/storage/v1${d.signedURL}` : null;
  } catch { return null; }
}

// ── Supabase client (lightweight, no SDK needed) ─────────────────
export const sb = {
  from: (table) => ({
    select: async (cols = "*") => {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=${cols}`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` },
      });
      return r.json();
    },
    insert: async (body) => {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=representation" },
        body: JSON.stringify(body),
      });
      return r.json();
    },
    update: (body, match) => {
      // Supports both: .update(body, match) and .update(body).eq(col, val)
      const doUpdate = async (matchObj) => {
        const qs = Object.entries(matchObj||{}).map(([k,v]) => `${k}=eq.${v}`).join("&");
        const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}${qs?"?"+qs:""}`, {
          method: "PATCH",
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=representation" },
          body: JSON.stringify(body),
        });
        return r.json();
      };
      if (match) return doUpdate(match);
      // Return chainable object with .eq()
      return {
        eq: (col, val) => doUpdate({ [col]: val }),
        then: (resolve) => doUpdate({}).then(resolve),
      };
    },
    delete: async (match) => {
      const qs = Object.entries(match).map(([k,v]) => `${k}=eq.${v}`).join("&");
      const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${qs}`, {
        method: "DELETE",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` },
      });
      return r.ok;
    },
  }),
  fn: async (name, body) => {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.json();
  },
};

// ── Supabase Auth client ──────────────────────────────────────────
export const sbAuth = {
  getSession: async () => {
    // Check localStorage for existing session token
    let token = localStorage.getItem("sb_session");
    if (!token) return null;
    // Try the token; if it's expired, refresh once before giving up (so a user away
    // for >1h stays logged in via the refresh token instead of being kicked out).
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const user = await r.json();
          const pr = await fetch(`${SUPABASE_URL}/rest/v1/user_profiles?id=eq.${user.id}&select=role,technician_id`, {
            headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
          });
          const profiles = await pr.json();
          const profile  = Array.isArray(profiles) && profiles[0];
          return { user, token, role: profile?.role||"owner", technician_id: profile?.technician_id||null };
        }
      } catch(e) {}
      if (attempt === 0) { const nt = await refreshSession(); if (nt) { token = nt; continue; } }
      break;
    }
    localStorage.removeItem("sb_session");
    localStorage.removeItem("sb_refresh");
    return null;
  },
  signIn: async (email, password) => {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await r.json();
    if (data.access_token) {
      localStorage.setItem("sb_session", data.access_token);
      if (data.refresh_token) localStorage.setItem("sb_refresh", data.refresh_token); // enables silent token refresh
      const pr = await fetch(`${SUPABASE_URL}/rest/v1/user_profiles?id=eq.${data.user.id}&select=role,technician_id`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${data.access_token}` },
      });
      const profiles = await pr.json();
      const profile  = Array.isArray(profiles) && profiles[0];
      return { user: data.user, token: data.access_token, role: profile?.role||"owner", technician_id: profile?.technician_id||null };
    }
    throw new Error(data.error_description || data.msg || "Invalid email or password");
  },
  // Operator self-signup (Phase B.2): creates the login AND a brand-new
  // company owned by this user. Routed through the create-operator-account
  // edge function because the new user has no session yet, so the browser
  // can't create the org/membership rows itself (RLS would block it).
  signUp: async (email, password, companyName, inviteCode) => {
    const data = await sb.fn("create-operator-account", {
      email,
      password,
      company_name: companyName,
      invite_code: inviteCode,   // verified server-side too (client check isn't real authz)
    });
    if (data.success) return data;
    throw new Error(data.error || "Registration failed");
  },
  signOut: async () => {
    const token = localStorage.getItem("sb_session");
    if (token) {
      await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
      }).catch(()=>{});
    }
    localStorage.removeItem("sb_session");
    localStorage.removeItem("sb_refresh");
  },
};
